import axios from "axios";
import { InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";

const storageKeys = {
  token: "token",
  user: "user",
  options: "options",
  starId: "StarId",
  studentVoted: "studentVoted",
};

const apiBase = process.env.REACT_APP_API_BASE;
const tenantId = process.env.REACT_APP_AZURE_TENANT_ID;
const clientId = process.env.REACT_APP_AZURE_CLIENT_ID;
const apiScope = process.env.REACT_APP_AZURE_API_SCOPE;
const redirectUri = process.env.REACT_APP_AZURE_REDIRECT_URI || "https://localhost:3000";
const postLogoutRedirectUri =
  process.env.REACT_APP_AZURE_POST_LOGOUT_REDIRECT_URI || `${redirectUri}/#/login`;

const msalConfig = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri,
  },
  cache: {
    cacheLocation: "localStorage",
  },
};

const msalInstance = new PublicClientApplication(msalConfig);
const loginRequest = {
  scopes: apiScope ? [apiScope] : [],
};

let initializePromise = null;
let refreshPromise = null;

const normalizeStarId = (value = "") => {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return "";
  }

  const domainSlash = trimmed.lastIndexOf("\\");
  const withoutDomain = domainSlash >= 0 ? trimmed.slice(domainSlash + 1) : trimmed;
  const atIndex = withoutDomain.indexOf("@");

  return atIndex > 0 ? withoutDomain.slice(0, atIndex) : withoutDomain;
};

const parseStoredJson = (key) => {
  const rawValue = localStorage.getItem(key);
  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue);
  } catch (error) {
    console.error(`Unable to parse localStorage value for ${key}.`, error);
    return null;
  }
};

const getCurrentAccount = () => {
  const activeAccount = msalInstance.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  const accounts = msalInstance.getAllAccounts();
  if (!accounts.length) {
    return null;
  }

  msalInstance.setActiveAccount(accounts[0]);
  return accounts[0];
};

const getCurrentAccountStarId = () => {
  const account = getCurrentAccount();
  const claimValue =
    account?.username ||
    account?.idTokenClaims?.preferred_username ||
    account?.idTokenClaims?.upn ||
    account?.idTokenClaims?.email ||
    "";

  return normalizeStarId(claimValue);
};

const parseJwtPayload = (token) => {
  if (!token) {
    return null;
  }

  try {
    const payload = token.split(".")[1];
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(base64);
    const json = decodeURIComponent(
      Array.from(decoded)
        .map((character) => `%${(`00${character.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join("")
    );

    return JSON.parse(json);
  } catch (error) {
    console.error("Unable to parse JWT payload.", error);
    return null;
  }
};

const clearLocalSession = () => {
  localStorage.removeItem(storageKeys.token);
  localStorage.removeItem(storageKeys.user);
  localStorage.removeItem(storageKeys.options);
  localStorage.removeItem(storageKeys.starId);
  localStorage.removeItem(storageKeys.studentVoted);
};

const storeSession = (session) => {
  if (!session) {
    clearLocalSession();
    return null;
  }

  const normalizedStarId = normalizeStarId(session.starId || getCurrentAccountStarId());
  const storedSession = {
    ...session,
    starId: normalizedStarId,
    roles: Array.isArray(session.roles) ? session.roles : [],
  };

  localStorage.setItem(storageKeys.token, JSON.stringify(storedSession));
  localStorage.setItem(storageKeys.starId, normalizedStarId);

  return storedSession;
};

const updateStoredAccessToken = (accessToken) => {
  if (!accessToken) {
    return null;
  }

  const existingSession = getCurrentToken() || {};
  const payload = parseJwtPayload(accessToken);
  const updatedSession = {
    ...existingSession,
    access_token: accessToken,
    token_type: existingSession.token_type || "bearer",
    expires: payload?.exp || existingSession.expires || 0,
    starId: existingSession.starId || getCurrentAccountStarId(),
    roles: Array.isArray(existingSession.roles) ? existingSession.roles : [],
  };

  return storeSession(updatedSession);
};

const isInteractionRequiredError = (error) =>
  error instanceof InteractionRequiredAuthError ||
  ["interaction_required", "consent_required", "login_required"].includes(error?.errorCode);

const fetchAuthenticationSession = async (accessToken) => {
  const response = await axios.get(`${apiBase}Authenticate`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    skipAuthorization: true,
    skipAuthRedirect: true,
  });

  const session = {
    ...response.data,
    access_token: accessToken,
  };

  if (!session.expires) {
    session.expires = parseJwtPayload(accessToken)?.exp || 0;
  }

  return storeSession(session);
};

const refreshAlreadyVotedFlag = async (session) => {
  if (!session?.starId) {
    localStorage.removeItem(storageKeys.studentVoted);
    return;
  }

  try {
    const response = await axios.get(`${apiBase}voted/${session.starId}`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
      skipAuthorization: true,
      skipAuthRedirect: true,
    });

    if (response.data) {
      localStorage.setItem(storageKeys.studentVoted, JSON.stringify(true));
    } else {
      localStorage.removeItem(storageKeys.studentVoted);
    }
  } catch (error) {
    localStorage.removeItem(storageKeys.studentVoted);
    console.error("Unable to refresh the voted flag.", error);
  }
};

const acquireAccessToken = async ({ interactive = false, forceRefresh = false } = {}) => {
  const account = getCurrentAccount();
  if (!account) {
    return null;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
      forceRefresh,
    });

    updateStoredAccessToken(response.accessToken);
    return response.accessToken;
  } catch (error) {
    if (!interactive && isInteractionRequiredError(error)) {
      return null;
    }

    if (interactive && isInteractionRequiredError(error)) {
      await msalInstance.acquireTokenRedirect(loginRequest);
      return null;
    }

    throw error;
  }
};

const refreshSession = async ({ interactive = false } = {}) => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const accessToken = await acquireAccessToken({ interactive });
    if (!accessToken) {
      return null;
    }

    const session = await fetchAuthenticationSession(accessToken);
    await refreshAlreadyVotedFlag(session);
    return session;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const initialize = async () => {
  if (initializePromise) {
    return initializePromise;
  }

  initializePromise = (async () => {
    const redirectResult = await msalInstance.handleRedirectPromise();
    if (redirectResult?.account) {
      msalInstance.setActiveAccount(redirectResult.account);
    } else {
      const account = getCurrentAccount();
      if (account) {
        msalInstance.setActiveAccount(account);
      }
    }

    if (!getCurrentAccount()) {
      clearLocalSession();
      return null;
    }

    try {
      return await refreshSession();
    } catch (error) {
      clearLocalSession();
      throw error;
    }
  })();

  return initializePromise;
};

const login = async () => {
  const existingAccount = getCurrentAccount();
  if (existingAccount) {
    const session = await refreshSession();
    if (session) {
      return session;
    }
  }

  await msalInstance.loginRedirect(loginRequest);
  return null;
};

const logout = async () => {
  const account = getCurrentAccount();
  clearLocalSession();

  if (!account) {
    if (typeof window !== "undefined") {
      window.location.hash = "#/login";
    }

    return;
  }

  await msalInstance.logoutRedirect({
    account,
    postLogoutRedirectUri,
  });
};

const logoutLocal = () => {
  clearLocalSession();
};

const getAccessToken = async (options = {}) => {
  const accessToken = await acquireAccessToken(options);
  if (!accessToken) {
    return null;
  }

  updateStoredAccessToken(accessToken);
  return accessToken;
};

const getCurrentToken = () => parseStoredJson(storageKeys.token);

const getCurrentUser = () => {
  const currentUser = parseStoredJson(storageKeys.user);
  return currentUser || getCurrentAccount();
};

const getStarId = () => {
  const currentToken = getCurrentToken();
  return currentToken?.starId || localStorage.getItem(storageKeys.starId) || getCurrentAccountStarId();
};

const alreadyVoted = () => parseStoredJson(storageKeys.studentVoted);

const hasRole = (roleName) => {
  const currentToken = getCurrentToken();
  return isTokenValid() && Array.isArray(currentToken?.roles) && currentToken.roles.includes(roleName);
};

const isAdmin = () => hasRole("Admin");

const isStudent = () => hasRole("Student");

const isCandidate = () => hasRole("Candidate");

const reloadPageAlreadyVoted = () => {
  const currentToken = getCurrentToken();
  if (currentToken?.roles?.includes("Admin")) {
    return "/mock";
  }

  return "/validate";
};

const reloadPage = () => {
  const currentToken = getCurrentToken();
  if (currentToken?.roles?.includes("Admin")) {
    return "/mock";
  }

  return "/";
};

const getLoginRedirectPath = () => {
  const currentToken = getCurrentToken();
  if (currentToken?.roles?.includes("Admin")) {
    return "/elections";
  }

  if (currentToken?.roles?.includes("Student")) {
    return "/";
  }

  return reloadPage();
};

const isTokenValid = () => {
  const currentToken = getCurrentToken();
  if (!currentToken) {
    return false;
  }

  if (currentToken.expires > Math.floor(Date.now() / 1000)) {
    return true;
  }

  return Boolean(getCurrentAccount());
};

const AuthService = {
  initialize,
  login,
  logout,
  logoutLocal,
  getAccessToken,
  getCurrentToken,
  isTokenValid,
  getCurrentUser,
  reloadPageAlreadyVoted,
  reloadPage,
  getLoginRedirectPath,
  isAdmin,
  isStudent,
  isCandidate,
  getStarId,
  alreadyVoted,
};

export default AuthService;
